import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiDownload,
  FiLoader,
  FiRefreshCcw,
  FiTrendingUp,
} from "react-icons/fi";
import axiosInstance from "../../../Utils/axiosInstance";

export const YEARS = [
  { label: "2026", value: "2026" },
  { label: "2025", value: "2025" },
];
const DEFAULT_MONTH = "Mar";
const DEFAULT_YEAR = "2026";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatValue = (value) => Number(value || 0).toFixed(2);

const formatDashboardTitle = (name) =>
  name
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()) || "Dashboard Detail";

// Pulls the consolidated CashIn report download link directly from the
// API-provided dashboard payload (dashboard.Main_EDA_Files[].xlsLink),
// instead of relying on a hardcoded URL.
const getConsolidatedReportLink = (dashboard) => {
  const files = dashboard?.Main_EDA_Files;
  if (!Array.isArray(files) || !files.length) return null;

  const match =
    files.find((f) =>
      (f?.id || f?.TableName || "").toLowerCase().includes("consolidated"),
    ) || files[0];

  return match?.xlsLink || match?.csvLink || null;
};

// ─── Month / chronology helpers ──────────────────────────────────────────────

const MONTH_ORDER = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

// Converts "Mar_2026" -> a sortable numeric value (year * 12 + monthIndex)
const parseMonthYear = (monthYear) => {
  if (!monthYear) return -Infinity;
  const [mon, year] = monthYear.split("_");
  const monthIndex = MONTH_ORDER[mon] ?? 0;
  return Number(year || 0) * 12 + monthIndex;
};

// Sorts an array of objects containing a MonthYear field in ascending
// chronological order (earliest month first, e.g. Jan, Feb, Mar, Apr)
const sortByMonthYear = (arr, key = "MonthYear") =>
  [...(arr || [])].sort(
    (a, b) => parseMonthYear(a[key]) - parseMonthYear(b[key]),
  );

// Finds the most recent MonthYear present in the API response data
// (this represents the "current" month as known by the API, not the
// real-world/system clock date). Since the array is now sorted ascending,
// the latest entry is the LAST item, not the first.
// const getLatestMonthYearFromApi = (chart1, chart5) => {
//   const source = chart1?.length ? chart1 : chart5;
//   if (!source?.length) return null;
//   const sorted = sortByMonthYear(source);
//   const latest = sorted[sorted.length - 1];
//   return latest?.MonthYear || null;
// };

// Calendar-previous Month/Year for a given Month/Year pair (e.g. Feb 2026 -> Jan 2026,
// Jan 2026 -> Dec 2025). Used to fetch the comparison period for chart2/chart3, since
// the API only ever returns project/group-wise rows for the single requested month —
// the previous month's breakdown has to be fetched separately.
const MONTH_VALUES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const getPreviousMonthYear = (month, year) => {
  const idx = MONTH_VALUES.indexOf(month);
  if (idx === -1) return { month, year };
  if (idx === 0) {
    return {
      month: MONTH_VALUES[MONTH_VALUES.length - 1],
      year: String(Number(year) - 1),
    };
  }
  return { month: MONTH_VALUES[idx - 1], year };
};

// ─── Card config ─────────────────────────────────────────────────────────────

const CARD_CONFIG = {
  Onshore: {
    accent: "bg-gradient-to-br from-[#0060D0] to-[#00C6A2]",
    icon: "🏗️",
    valueColor: "text-white",
  },
  Service: {
    accent: "bg-gradient-to-br from-[#6d4cf5] to-[#3ba3ec]",
    icon: "🛠️",
    valueColor: "text-white",
  },
  "Advance on New Orders": {
    accent: "bg-gradient-to-br from-[#d97706] to-[#f59e0b]",
    icon: "📦",
    valueColor: "text-white",
  },
};

const getCardConfig = (cardName) =>
  CARD_CONFIG[cardName] || {
    accent: "bg-gradient-to-br from-slate-500 to-slate-700",
    icon: "📊",
    valueColor: "text-white",
  };

// ─── Default / fallback data ─────────────────────────────────────────────────

const defaultCards = [
  { cardName: "Onshore", cardValue: "—", monthYear: "" },
  { cardName: "Service", cardValue: "—", monthYear: "" },
  { cardName: "Advance on New Orders", cardValue: "—", monthYear: "" },
];

const defaultData = {
  cards: defaultCards,
  chart1: [],
  chart2: [],
  chart2Prev: [],
  chart3: [],
  chart3Prev: [],
  chart4: [],
  chart5: [],
};

// ─── Data builder ────────────────────────────────────────────────────────────

const buildDashboardData = (items) => ({
  cards: [
    items?.card1?.[0] || { cardName: "Onshore", cardValue: "0" },
    items?.card2?.[0] || { cardName: "Service", cardValue: "0" },
    items?.card3?.[0] || { cardName: "Advance on New Orders", cardValue: "0" },
  ],
  chart1: items?.chart1 || [],
  chart2: items?.chart2 || [],
  chart3: items?.chart3 || [],
  chart4: items?.chart4 || [],
  chart5: items?.chart5 || [],
});

// ─── Chart option builders ────────────────────────────────────────────────────

const getLineChartOption = (chart1) => {
  // Months shown in ascending sequence (e.g. Jan, Feb, Mar, Apr)
  const sortedChart1 = sortByMonthYear(chart1);
  const labels = sortedChart1.map((d) => d.MonthYear?.replace("_", " ") || "");
  return {
    animation: false,
    tooltip: {
      confine: true,
      trigger: "axis",
      axisPointer: {
        type: "line",
        lineStyle: { color: "#cbd5e1", type: "dashed" },
      },
      backgroundColor: "rgba(16, 16, 16, 0.92)",
      borderWidth: 0,
      textStyle: { color: "#f8f5f5", fontSize: 13, fontWeight: "bold" },
    },
    legend: {
      top: 0,
      right: 10,
      icon: "circle",
      textStyle: {
        color: "#000",
        fontSize: 13,
        fontWeight: "bold",
      },
    },
    grid: {
      left: 50,
      right: 20,
      top: 45,
      bottom: 40,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: labels,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisTick: { show: false },
      axisLabel: {
        color: "#000",
        fontSize: 11,
        fontWeight: "bold",
        margin: 12,
      },
      splitLine: { show: true, lineStyle: { color: "#eef2ff" } },
    },
    yAxis: {
      type: "value",
      name: "Rs Cr",
      nameTextStyle: { color: "#94a3b8", fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#000", fontSize: 10, fontWeight: "bold" },
      splitLine: { lineStyle: { color: "#e6ecfb" } },
    },
    series: [
      {
        name: "Onshore",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
        data: sortedChart1.map((d) => Number(d.OnshoreActual || 0)),
        lineStyle: { width: 3, color: "#6FC2A7" },
        itemStyle: { color: "#6FC2A7" },
        areaStyle: {
          opacity: 0.12,
        },
      },
      {
        name: "Service",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
        data: sortedChart1.map((d) => Number(d.ServiceActual || 0)),
        lineStyle: { width: 3, color: "#6D5EF7" },
        itemStyle: { color: "#6D5EF7" },
      },
      {
        name: "Advance",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
        data: sortedChart1.map((d) => Number(d.AdvanceActual || 0)),
        lineStyle: { width: 3, color: "#3B82F6" },
        itemStyle: { color: "#3B82F6" },
      },
    ],
  };
};

// Builds a SINGLE-series bar chart for the current month's data only.
// (Previously this rendered a grouped/dual bar comparing current vs
// previous month — now it shows just the current period as one bar
// per category.) `previousData` is still accepted so call-sites and the
// rest of the file (chart2Prev / chart3Prev fetching) remain unchanged,
// but it is no longer used to render a second bar series.
const getBarChartOption = (
  currentData,
  previousData,
  xKey,
  yKey,
  color,
  title,
) => {
  // Filter out aggregate/total rows for cleaner charts
  const EXCLUDE = ["total", "grand total"];
  const filterRows = (rows) =>
    (rows || []).filter(
      (d) => !EXCLUDE.some((ex) => (d[xKey] || "").toLowerCase().includes(ex)),
    );

  const currentRows = filterRows(currentData);

  const currentMonthYear = currentRows[0]?.MonthYear;

  // Category order: current month's categories sorted descending by value
  const categoryOrder = [...currentRows]
    .sort((a, b) => Number(b[yKey] || 0) - Number(a[yKey] || 0))
    .map((d) => d[xKey]);

  const currentLookup = {};
  currentRows.forEach((d) => {
    currentLookup[d[xKey]] = Number(d[yKey] || 0);
  });

  const currentLabel = currentMonthYear?.replace("_", " ") || "Current";

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      confine: true,
      axisPointer: { type: "shadow" },
      backgroundColor: "rgba(15,23,42,0.92)",
      borderWidth: 0,
      textStyle: { color: "#f8fafc", fontSize: 12, fontWeight: "bold" },
      formatter: (params) => {
        const name = params?.[0]?.name || "";
        const lines = params
          .map(
            (p) =>
              `${p.marker} ${p.seriesName}: <b>₹${Number(p.value).toFixed(2)} Cr</b>`,
          )
          .join("<br/>");
        return `${name}<br/>${lines}`;
      },
    },
    legend: {
      top: 0,
      right: 10,
      icon: "circle",
      textStyle: { color: "#000", fontSize: 12, fontWeight: "bold" },
    },
    grid: { left: 55, right: 18, top: 45, bottom: 80 },
    xAxis: {
      type: "category",
      data: categoryOrder,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisTick: { show: false },
      axisLabel: {
        color: "#000",
        fontSize: 10,
        fontWeight: "bold",
        interval: 0,
        rotate: 35,
        overflow: "truncate",
        width: 80,
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "Rs Cr",
      nameTextStyle: { color: "#94a3b8", fontSize: 10 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#000", fontSize: 10, fontWeight: "bold" },
      splitLine: { lineStyle: { color: "#e6ecfb" } },
    },
    series: [
      {
        name: currentLabel,
        type: "bar",
        barWidth: 24,
        data: categoryOrder.map((cat) => currentLookup[cat] ?? 0),
        itemStyle: {
          borderRadius: [6, 6, 0, 0],
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color },
              { offset: 1, color: `${color}CC` },
            ],
          },
        },
      },
    ],
  };
};

const getDonutChartOption = (chart4) => {
  const filtered = chart4
    .filter((d) => !(d.Name || "").toLowerCase().includes("grand total"))
    // Slices sorted descending by value
    .sort((a, b) => Number(b.Values || 0) - Number(a.Values || 0));
  const total =
    chart4.find((d) => (d.Name || "").toLowerCase().includes("grand total"))
      ?.Values || 0;

  const COLORS = ["#d97706", "#f59e0b", "#0060D0", "#6d4cf5", "#35a57f"];

  return {
    animation: false,
    tooltip: {
      trigger: "item",
      confine: true,
      formatter: ({ name, value, percent }) =>
        `${name}<br/>₹${Number(value).toFixed(2)} Cr (${percent}%)`,
      backgroundColor: "rgba(15,23,42,0.92)",
      borderWidth: 0,
      textStyle: { color: "#f8fafc", fontSize: 12, fontWeight: "bold" },
    },
    series: [
      {
        type: "pie",
        radius: ["65%", "82%"],
        center: ["50%", "45%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: {
          borderColor: "#fff",
          borderWidth: 4,
          borderRadius: 6,
        },
        data: filtered.map((d, i) => ({
          name: d.Name,
          value: Number(d.Values || 0),
          itemStyle: {
            color: COLORS[i % COLORS.length],
          },
        })),
      },
    ],
    graphic: [
      {
        type: "text",
        left: "center",
        top: "38%",
        style: {
          text: Number(total).toFixed(2),
          fontSize: 28,
          fontWeight: 700,
          fill: "#111827",
        },
      },
      {
        type: "text",
        left: "center",
        top: "54%",
        style: {
          text: "Total",
          fontSize: 12,
          fill: "#94a3b8",
        },
      },
    ],
  };
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SummaryCard = ({ card, onClick, loading }) => {
  const cfg = getCardConfig(card.cardName);
  return (
    <div
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md cursor-pointer"
      onClick={onClick}
    >
      <div className={`p-6 text-white ${cfg.accent}`}>
        <div className="mb-2 flex items-center gap-2">
          <span className="text-lg">{cfg.icon}</span>
          <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
            {card.cardName}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          {loading ? (
            <>
              <FiLoader className="h-8 w-8 animate-spin opacity-80" />
              <span className="text-lg opacity-80">Loading...</span>
            </>
          ) : (
            <>
              <span className="text-4xl font-bold leading-none">
                {Number(card?.cardValue ?? 0).toFixed(0)}
              </span>
              <span className="text-xl opacity-80">Rs Cr</span>
            </>
          )}
        </div>
        <p className="mt-1 text-xs opacity-70">
          {card.monthYear?.replace("_", " ") || ""}
        </p>
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, loading }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-2">
      <h2
        className="text-lg font-extrabold"
        style={{
          color: "#000",
          fontWeight: 800,
        }}
      >
        {title}
      </h2>

      {subtitle && (
        <p
          className="text-sm mt-1"
          style={{
            color: "#000",
            fontWeight: 700,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>

    {children}
  </div>
);

const CashInTable = ({ chart5, reportLink }) => {
  if (!chart5?.length) return null;

  // Build columns from available MonthYear entries
  // sorted in ascending chronological sequence (e.g. Jan, Feb, Mar, Apr)
  const sorted = sortByMonthYear(chart5);

  // Collect all unique categories preserving order (Total Collection last)
  const categorySet = new Set();
  sorted.forEach((entry) => {
    entry.data?.forEach((row) => {
      if (row.Category !== "Total Collection") categorySet.add(row.Category);
    });
  });
  const categories = [...categorySet, "Total Collection"];

  // Build lookup: monthYear -> { Category -> Value }
  const lookup = {};
  sorted.forEach((entry) => {
    lookup[entry.MonthYear] = {};
    entry.data?.forEach((row) => {
      lookup[entry.MonthYear][row.Category] = Number(row.Value || 0);
    });
  });

  const categoryColors = {
    Onshore: "text-blue-600",
    Service: "text-emerald-600",
    "Advance on New Orders": "text-amber-600",
    "Total Collection": "text-slate-800",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Overall Cash In Summary
        </p>
        <h2 className="text-base font-semibold text-slate-800 mt-0.5">
          Cash In – Service &amp; Onshore (All Months)
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="py-3 pl-5 pr-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-400 w-8">
                #
              </th>
              <th className="py-3 px-4 text-left font-semibold text-xs uppercase tracking-wider text-slate-400">
                Category
              </th>
              {sorted.map((entry) => (
                <th
                  key={entry.MonthYear}
                  className="py-3 px-4 text-right font-semibold text-xs uppercase tracking-wider text-slate-400"
                >
                  {entry.MonthYear?.replace("_", " ")} Actual
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, idx) => {
              const isTotal = cat === "Total Collection";
              return (
                <tr
                  key={cat}
                  className={`border-b border-slate-100 ${
                    isTotal ? "bg-slate-50 font-bold" : "hover:bg-slate-50/50"
                  }`}
                >
                  <td className="py-3 pl-5 pr-2 text-slate-400">
                    {isTotal ? "—" : idx + 1}
                  </td>
                  <td
                    className={`py-3 px-4 font-medium ${
                      categoryColors[cat] || "text-slate-700"
                    }`}
                  >
                    {cat}
                  </td>
                  {sorted.map((entry) => (
                    <td
                      key={entry.MonthYear}
                      className={`py-3 px-4 text-right tabular-nums ${
                        isTotal ? "text-slate-800" : "text-slate-600"
                      }`}
                    >
                      {(lookup[entry.MonthYear]?.[cat] ?? 0).toFixed(2)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Download consolidated report (bottom-right) ──
          Link comes from the API response (dashboard.Main_EDA_Files),
          not a hardcoded URL. Button is disabled if the API doesn't
          provide a link. */}
      <div className="flex justify-end px-5 py-3 border-t border-slate-100 bg-slate-50">
        {reportLink ? (
          <a
            href={reportLink}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-[#0060D0] px-4 py-2 text-sm font-semibold !text-white shadow-sm transition hover:bg-[#0050b0]"
          >
            <FiDownload className="h-4 w-4" />
            Download Final Consolidated Report
          </a>
        ) : (
          <button
            type="button"
            disabled
            title="Report link not available from API"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-300 px-4 py-2 text-sm font-semibold text-white shadow-sm cursor-not-allowed"
          >
            <FiDownload className="h-4 w-4" />
            Download Report
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const EDA3CashInDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [dashboardData, setDashboardData] = useState(defaultData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // The "current" month/year as reported by the API (i.e. the most recent
  // MonthYear present in the API response), used for the filter default
  // and the Reset button — NOT derived from the real-time system clock.
  const [apiCurrentMonth, setApiCurrentMonth] = useState(null);
  const [apiCurrentYear, setApiCurrentYear] = useState(null);
  // const hasSetApiCurrentRef = useRef(false);

  const { dashboard, selectedProcess } = location.state || {};
  const title = formatDashboardTitle(dashboard?.eda_name);
  const selectedMonthLabel =
    months.find((m) => m.value === selectedMonth)?.label || "";

  // Consolidated CashIn report download link, sourced directly from the
  // dashboard object passed in via API (dashboard.Main_EDA_Files[].xlsLink)
  // instead of a hardcoded URL.
  const consolidatedReportLink = useMemo(
    () => getConsolidatedReportLink(dashboard),
    [dashboard],
  );

  // Memoised chart options
  const lineChartOption = useMemo(
    () => getLineChartOption(dashboardData.chart1),
    [dashboardData.chart1],
  );

  const barChart2Option = useMemo(
    () =>
      getBarChartOption(
        dashboardData.chart2,
        dashboardData.chart2Prev,
        "PROJECT",
        "Value",
        "#0060D0",
        "Onshore Project-wise",
      ),
    [dashboardData.chart2, dashboardData.chart2Prev],
  );

  const barChart3Option = useMemo(
    () =>
      getBarChartOption(
        dashboardData.chart3,
        dashboardData.chart3Prev,
        "Group",
        "Value",
        "#35a57f",
        "Service Group-wise",
      ),
    [dashboardData.chart3, dashboardData.chart3Prev],
  );

  const donutChart4Option = useMemo(
    () => getDonutChartOption(dashboardData.chart4),
    [dashboardData.chart4],
  );

  // chart4 legend items (excluding grand total), sorted descending by value
  // to match the donut slice ordering
  const chart4Legend = useMemo(
    () =>
      dashboardData.chart4
        .filter((d) => !(d.Name || "").toLowerCase().includes("grand total"))
        .sort((a, b) => Number(b.Values || 0) - Number(a.Values || 0))
        .map((d, i) => ({
          name: d.Name,
          value: d.Values,
          color: ["#d97706", "#f59e0b", "#0060D0", "#6d4cf5", "#35a57f"][i % 5],
        })),
    [dashboardData.chart4],
  );

  useEffect(() => {
  const fetchMonths = async () => {
    try {
      const response = await axiosInstance.get(
        "/eda3/eda3cashinmonths"
      );

      if (response?.data?.status) {
        setMonths(response.data.items);

        const currentMonth = response.data.items.find((item) =>
          item.label.startsWith("Current Month")
        );

        if (currentMonth) {
          setSelectedMonth(currentMonth.value);
          setApiCurrentMonth(currentMonth.value);
        }
      }
    } catch (error) {
      console.error("Failed to fetch months", error);
    }
  };

  fetchMonths();
}, []);

  useEffect(() => {
     if (!location.state || !selectedMonth) return;

    let ignore = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const prev = getPreviousMonthYear(selectedMonth, selectedYear);

        const [response, prevResponse] = await Promise.all([
          axiosInstance.post("/eda3/eda3cashIn", {
            Month: selectedMonth,
            Year: selectedYear,
          }),
          // Previous month is fetched purely to populate the comparison bars
          // in chart2/chart3 — if it fails, the current month still renders.
          axiosInstance
            .post("/eda3/eda3cashIn", {
              Month: prev.month,
              Year: prev.year,
            })
            .catch(() => null),
        ]);

        if (!ignore && response?.data?.status && response?.data?.items) {
          const built = buildDashboardData(response.data.items);

          const prevItems = prevResponse?.data?.status
            ? prevResponse.data.items
            : null;
          built.chart2Prev = prevItems?.chart2 || [];
          built.chart3Prev = prevItems?.chart3 || [];

          setDashboardData(built);

          // Determine the "current" month/year from the API data itself
          // (the latest MonthYear available), only once on initial load.
          // if (!hasSetApiCurrentRef.current) {
          //   const latestMonthYear = getLatestMonthYearFromApi(
          //     built.chart1,
          //     built.chart5,
          //   );
          //   if (latestMonthYear) {
          //     const [latestMon, latestYear] = latestMonthYear.split("_");
          //     hasSetApiCurrentRef.current = true;
          //     setApiCurrentMonth(latestMon);
          //     setApiCurrentYear(latestYear);
          //     setSelectedMonth(latestMon);
          //     setSelectedYear(latestYear);
          //   } else {
          //     hasSetApiCurrentRef.current = true;
          //   }
          // }
        }
      } catch (err) {
        if (!ignore) {
          setError(
            err?.response?.data?.message || "Failed to fetch Cash In data.",
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();
    return () => {
      ignore = true;
    };
  }, [location.state, selectedMonth, selectedYear]);

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Process: {formatDashboardTitle(selectedProcess)} | Status:{" "}
              {dashboard?.edaStatus || "Available"}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>

        {!location.state ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Open this page from the dashboard list so the selected dashboard
            context is available.
          </div>
        ) : (
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              {/* Year */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={loading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEARS.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">
                  Month
                </span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={loading}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reset */}
              <button
                type="button"
                onClick={() => {
                  setSelectedMonth(apiCurrentMonth);
                }}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset to current month
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {loading && (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Updating dashboard...
                </div>
              )}
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                <FiCalendar className="h-4 w-4" />
                Current period: {selectedMonthLabel} {selectedYear}
              </div>
            </div>
          </div>
        )}
      </div>

      {location.state && (
        <>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* ── Cards ── */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {dashboardData.cards.map((card) => (
              <SummaryCard
                key={card.cardName}
                card={card}
                loading={loading}
                onClick={() => {
                  // console.log("CARD CLICKED");

                  navigate("/Insights/eda-CashInsegment-detail", {
                    state: {
                      selectedProcess,
                      dashboard,
                      segment: card.cardName,
                      month: selectedMonth,
                      year: selectedYear,
                    },
                  });
                }}
              />
            ))}
          </div>

          {/* ── Line Chart (chart1) ── */}
          <ChartCard
            title="Monthly Cash In Trend – Onshore, Service & Advance"
            subtitle="Actuals Rs Cr across available months"
          >
            <div className="mt-3 overflow-x-auto">
              <div className="min-w-[600px]">
                <ReactECharts
                  option={lineChartOption}
                  notMerge
                  lazyUpdate
                  showLoading={loading}
                  style={{ height: 300, width: "100%" }}
                />
              </div>
            </div>
          </ChartCard>

          {/* ── Bar Charts (chart2 + chart3) + Donut (chart4) ── */}
          <div className="grid grid-cols-1 gap-5 2xl:grid-cols-3">
            {/* chart2 – Onshore Project-wise */}
            <ChartCard
              title="Onshore — Project-wise"
              subtitle={`${selectedMonthLabel} ${selectedYear} · Rs Cr`}
            >
              <div className="mt-3">
                <ReactECharts
                  option={barChart2Option}
                  notMerge
                  lazyUpdate
                  showLoading={loading}
                  style={{ height: 280, width: "100%" }}
                />
              </div>
            </ChartCard>

            {/* chart3 – Service Group-wise */}
            <ChartCard
              title="Service — Group-wise"
              subtitle={`${selectedMonthLabel} ${selectedYear} · Rs Cr`}
            >
              <div className="mt-3">
                <ReactECharts
                  option={barChart3Option}
                  notMerge
                  lazyUpdate
                  showLoading={loading}
                  style={{ height: 280, width: "100%" }}
                />
              </div>
            </ChartCard>

            {/* chart4 – Advance Customer Split (Donut) */}
            <ChartCard
              title="Advance — Customer Split"
              subtitle={`${selectedMonthLabel} ${selectedYear} · Rs Cr`}
            >
              <div className="mt-3">
                <ReactECharts
                  option={donutChart4Option}
                  notMerge
                  lazyUpdate
                  showLoading={loading}
                  style={{ height: 220, width: "100%" }}
                />
              </div>
              <div className="mt-3 space-y-2">
                {chart4Legend.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 text-sm last:border-b-0"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                        style={{ backgroundColor: item.color }}
                      />
                      <span
                        className="truncate text-slate-500 font-bold"
                        style={{
                          color: "#000",
                          fontWeight: 700,
                        }}
                      >
                        {item.name}
                      </span>
                    </div>
                    <span
                      className="font-mono text-slate-700 shrink-0"
                      style={{
                        color: "#000",
                        fontWeight: 700,
                      }}
                    >
                      ₹{Number(item.value || 0).toFixed(2)} Cr
                    </span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          {/* ── Table (chart5) ── */}
          <CashInTable
            chart5={dashboardData.chart5}
            reportLink={consolidatedReportLink}
          />
        </>
      )}
    </div>
  );
};

export default EDA3CashInDashboard;